import {
  API_PREFIX,
  ErrorCode,
  errorMessage,
  type ApiError,
  type AuthTokens,
} from '@forever/shared';

/**
 * Panel API istemcisi. Geliştirmede Vite aynı kökene vekillik ettiği için
 * göreli yol yeterli; üretimde VITE_API_URL ile tam adres verilir.
 */
const RAW_BASE = import.meta.env.VITE_API_URL ?? '';
export const API_BASE = `${RAW_BASE.replace(/\/$/, '')}${API_PREFIX}`;
export const SOCKET_URL = RAW_BASE || window.location.origin;

const TOKEN_KEY = 'forever.admin.tokens';

export class ApiRequestError extends Error {
  readonly code: ErrorCode | string;
  readonly status: number;
  readonly fields?: Record<string, string>;

  constructor(status: number, body: ApiError['error'] | null) {
    super(body?.message ?? errorMessage(ErrorCode.INTERNAL_ERROR));
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.code ?? ErrorCode.INTERNAL_ERROR;
    this.fields = body?.fields;
  }
}

export function getTokens(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as AuthTokens) : null;
  } catch {
    return null;
  }
}

export function setTokens(tokens: AuthTokens): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Oturum düşünce panelin tamamı tepki versin diye yayınlanan olay. */
export const SESSION_EXPIRED_EVENT = 'forever:session-expired';

let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  const tokens = getTokens();
  if (!tokens?.refreshToken) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken: tokens.refreshToken }),
    });
    if (!res.ok) return false;
    setTokens((await res.json()) as AuthTokens);
    return true;
  } catch {
    return false;
  }
}

/**
 * Erişim jetonu bitmek üzereyse (ya da bittiyse) yeniler. Aynı anda gelen
 * çağrılar tek yenileme isteğini paylaşır. Soket yeniden bağlanmadan önce
 * kullanılır: sunucu, jetonu biten veya yenilenen oturumun soketini kapatır.
 */
export async function ensureFreshSession(marginMs = 30_000): Promise<boolean> {
  const tokens = getTokens();
  if (!tokens) return false;
  const expiresAt = Date.parse(tokens.expiresAt);
  if (Number.isFinite(expiresAt) && expiresAt - Date.now() > marginMs) return true;
  refreshPromise ??= refreshSession().finally(() => {
    refreshPromise = null;
  });
  const ok = await refreshPromise;
  if (!ok) {
    clearTokens();
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
  return ok;
}

type Query = Record<string, string | number | boolean | undefined | null>;

type Options = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Query;
  anonymous?: boolean;
  /** Dosya indirme uçlarında ham yanıt gerekir. */
  raw?: boolean;
  signal?: AbortSignal;
};

function buildUrl(path: string, query?: Query): string {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function request<T>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, query, anonymous = false, raw = false, signal } = options;

  const send = async (retrying = false): Promise<T> => {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (!anonymous) {
      const tokens = getTokens();
      if (tokens?.accessToken) headers.authorization = `Bearer ${tokens.accessToken}`;
    }

    const res = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });

    if (raw) {
      if (!res.ok) throw new ApiRequestError(res.status, null);
      return res as unknown as T;
    }

    if (res.status === 204) return undefined as T;

    const text = await res.text();
    const parsed = text ? (JSON.parse(text) as unknown) : null;

    if (res.ok) return parsed as T;

    const errorBody = (parsed as ApiError | null)?.error ?? null;

    if (res.status === 401 && !anonymous && !retrying) {
      refreshPromise ??= refreshSession().finally(() => {
        refreshPromise = null;
      });
      if (await refreshPromise) return send(true);
      clearTokens();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }

    throw new ApiRequestError(res.status, errorBody);
  };

  return send();
}

export const api = {
  get: <T>(path: string, query?: Query, opts?: Options) =>
    request<T>(path, { ...opts, method: 'GET', query }),
  post: <T>(path: string, body?: unknown, opts?: Options) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: Options) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: Options) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: Options) => request<T>(path, { ...opts, method: 'DELETE' }),
};

/** Dosya indirme — CSV rapor ve KVKK dışa aktarımı için. */
export async function downloadFile(
  path: string,
  query: Query,
  fallbackName: string,
): Promise<void> {
  const res = await request<Response>(path, { query, raw: true });
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const name = match?.[1] ? decodeURIComponent(match[1]) : fallbackName;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function messageFromError(e: unknown): string {
  if (e instanceof ApiRequestError) return e.message;
  if (e instanceof Error) return e.message;
  return errorMessage(ErrorCode.INTERNAL_ERROR);
}

export function fieldsFromError(e: unknown): Record<string, string> | undefined {
  return e instanceof ApiRequestError ? e.fields : undefined;
}
